import { GridColumn, GridItem, GridRow } from "commons/ui/Grid";
import InputText from "commons/ui/input/InputText";
import IntegerInput from "commons/ui/input/IntegerInput";
import Select from "commons/ui/input/Select";
import SwitchInput from "commons/ui/input/SwitchInput";
import WysiwygSimpleEditor from "commons/ui/input/WysiwygSimpleEditor";
import Paper from "commons/ui/Paper";

import ExtensionsProporsionInfo from "components/subComponents/ExtensionsProporsionInfo";
import TwoLanguagesAdornment from "components/subComponents/TwoLanguagesAdornment";
import AttachmentPicker from "components/ui/AttachmentPicker";

import useMaterialForm from "hooks/form/useMaterialForm";

import { DesktopOrMobile } from "types/enums";

function MaterialForm({ materialId }: { materialId?: string }) {
    const {
        formHook,
        setThumbImage,
        setMainImage,
        typeOptions,
        categoryOptions,
        subcategoryOptions,
    } = useMaterialForm({ materialId });

    const { checkErrorVisibility, setValue, form } = formHook;

    return (
        <GridItem>
            <GridRow>
                <GridItem className={"col-xs-12-12"}>
                    <Paper>
                        <GridColumn>
                            <GridRow className="title-margin">
                                <GridItem>
                                    <h2>Nome</h2>
                                </GridItem>
                            </GridRow>

                            <GridRow>
                                <GridItem className={"col-xs-12-6"}>
                                    <InputText
                                        label={"Art RR"}
                                        startAdornment={TwoLanguagesAdornment("it")}
                                        value={form.text.it.art_rr}
                                        setValue={setValue}
                                        path={"text.it.art_rr"}
                                        checkErrorVisibility={checkErrorVisibility}
                                    />
                                </GridItem>

                                <GridItem className={"col-xs-12-6"}>
                                    <InputText
                                        label={"Codice RR"}
                                        startAdornment={TwoLanguagesAdornment("it")}
                                        value={form.text.it.code_rr}
                                        setValue={setValue}
                                        path={"text.it.code_rr"}
                                        checkErrorVisibility={checkErrorVisibility}
                                    />
                                </GridItem>
                            </GridRow>

                            <GridRow>
                                <GridItem className={"col-xs-12-6"}>
                                    <InputText
                                        label={"Art RR"}
                                        startAdornment={TwoLanguagesAdornment("en")}
                                        value={form.text.en.art_rr}
                                        setValue={setValue}
                                        path={"text.en.art_rr"}
                                        checkErrorVisibility={checkErrorVisibility}
                                    />
                                </GridItem>

                                <GridItem className={"col-xs-12-6"}>
                                    <InputText
                                        label={"Codice RR"}
                                        startAdornment={TwoLanguagesAdornment("en")}
                                        value={form.text.en.code_rr}
                                        setValue={setValue}
                                        path={"text.en.code_rr"}
                                        checkErrorVisibility={checkErrorVisibility}
                                    />
                                </GridItem>
                            </GridRow>

                            <GridRow className="title-margin">
                                <GridItem>
                                    <h2>Impostazioni</h2>
                                </GridItem>
                            </GridRow>

                            <GridRow>
                                <GridItem className={"col-xs-12-3"}>
                                    <Select
                                        label={"Tipologia"}
                                        options={typeOptions}
                                        value={form.type ?? ""}
                                        setValue={setValue}
                                        path={"type"}
                                        checkErrorVisibility={checkErrorVisibility}
                                    />
                                </GridItem>

                                <GridItem className={"col-xs-12-3"}>
                                    <Select
                                        label={"Categoria"}
                                        options={categoryOptions}
                                        value={form.material_category_id ?? ""}
                                        setValue={setValue}
                                        path={"material_category_id"}
                                        checkErrorVisibility={checkErrorVisibility}
                                    />
                                </GridItem>

                                <GridItem className={"col-xs-12-3"}>
                                    <Select
                                        label={"Sottocategoria"}
                                        options={subcategoryOptions}
                                        value={form.material_subcategory_id ?? ""}
                                        setValue={setValue}
                                        path={"material_subcategory_id"}
                                        checkErrorVisibility={checkErrorVisibility}
                                    />
                                </GridItem>

                                <GridItem className={"col-xs-24-4"}>
                                    <IntegerInput
                                        label={"Ordine"}
                                        value={form.graphical_order}
                                        setValue={setValue}
                                        path={"graphical_order"}
                                        checkErrorVisibility={checkErrorVisibility}
                                    />
                                </GridItem>

                                <GridItem className={"col-xs-24-2"}>
                                    <SwitchInput
                                        checked={form.enabled}
                                        label={"Abilitato"}
                                        setValue={setValue}
                                        path={"enabled"}
                                        alignedToForm
                                    />
                                </GridItem>
                            </GridRow>

                            <GridRow>
                                <GridItem className={"col-xs-12-4"}>
                                    <InputText
                                        label={"Articolo"}
                                        value={form.article}
                                        setValue={setValue}
                                        path={"article"}
                                        checkErrorVisibility={checkErrorVisibility}
                                    />
                                </GridItem>

                                <GridItem className={"col-xs-12-4"}>
                                    <InputText
                                        label={"Codice"}
                                        value={form.code}
                                        setValue={setValue}
                                        path={"code"}
                                        checkErrorVisibility={checkErrorVisibility}
                                    />
                                </GridItem>

                                <GridItem className={"col-xs-12-4"}>
                                    <InputText
                                        label={"Cat"}
                                        value={form.cat}
                                        setValue={setValue}
                                        path={"cat"}
                                        checkErrorVisibility={checkErrorVisibility}
                                    />
                                </GridItem>
                            </GridRow>

                            <GridRow className="title-margin">
                                <GridItem>
                                    <h2>Immagine anteprima (Thumb)</h2>
                                </GridItem>
                            </GridRow>

                            <ExtensionsProporsionInfo
                                desktopText={"Desktop: consigliato JPG/PNG (max 200KB)"}
                                mobileText={"Mobile: consigliato JPG/PNG (max 150KB)"}
                                proportion={""}
                            />

                            <GridRow>
                                <GridItem className={"col-xs-12-6"}>
                                    <AttachmentPicker
                                        buttonLabel={"Foto Desktop"}
                                        setFile={(file) =>
                                            setThumbImage(file, DesktopOrMobile.desktop)
                                        }
                                        file={form.thumb_img.desktop}
                                        accept={".png,.jpg,.jpeg"}
                                        errors={checkErrorVisibility("thumb_img.desktop")}
                                    />
                                </GridItem>

                                <GridItem className={"col-xs-12-6"}>
                                    <AttachmentPicker
                                        buttonLabel={"Foto Mobile"}
                                        setFile={(file) =>
                                            setThumbImage(file, DesktopOrMobile.mobile)
                                        }
                                        file={form.thumb_img.mobile}
                                        accept={".png,.jpg,.jpeg"}
                                        errors={checkErrorVisibility("thumb_img.mobile")}
                                    />
                                </GridItem>
                            </GridRow>

                            <GridRow className="title-margin">
                                <GridItem>
                                    <h2>Immagine principale</h2>
                                </GridItem>
                            </GridRow>

                            <ExtensionsProporsionInfo
                                desktopText={"Desktop: consigliato JPG/PNG (max 500KB)"}
                                mobileText={"Mobile: consigliato JPG/PNG (max 300KB)"}
                                proportion={""}
                            />

                            <GridRow>
                                <GridItem className={"col-xs-12-6"}>
                                    <AttachmentPicker
                                        buttonLabel={"Foto Desktop"}
                                        setFile={(file) =>
                                            setMainImage(file, DesktopOrMobile.desktop)
                                        }
                                        file={form.main_img.desktop}
                                        accept={".png,.jpg,.jpeg"}
                                        errors={checkErrorVisibility("main_img.desktop")}
                                    />
                                </GridItem>

                                <GridItem className={"col-xs-12-6"}>
                                    <AttachmentPicker
                                        buttonLabel={"Foto Mobile"}
                                        setFile={(file) =>
                                            setMainImage(file, DesktopOrMobile.mobile)
                                        }
                                        file={form.main_img.mobile}
                                        accept={".png,.jpg,.jpeg"}
                                        errors={checkErrorVisibility("main_img.mobile")}
                                    />
                                </GridItem>
                            </GridRow>

                            <GridRow className="title-margin">
                                <GridItem>
                                    <h2>Italiano</h2>
                                </GridItem>
                            </GridRow>

                            <GridRow>
                                <GridItem className={"col-xs-12-12"}>
                                    <WysiwygSimpleEditor
                                        label={"Descrizione"}
                                        value={form.text.it.description}
                                        setValue={setValue}
                                        path={"text.it.description"}
                                        checkErrorVisibility={checkErrorVisibility}
                                    />
                                </GridItem>
                            </GridRow>

                            <GridRow>
                                <GridItem className={"col-xs-12-12"}>
                                    <WysiwygSimpleEditor
                                        label={"Manutenzione"}
                                        value={form.text.it.maintenance}
                                        setValue={setValue}
                                        path={"text.it.maintenance"}
                                        checkErrorVisibility={checkErrorVisibility}
                                    />
                                </GridItem>
                            </GridRow>

                            <GridRow>
                                <GridItem className={"col-xs-12-12"}>
                                    <WysiwygSimpleEditor
                                        label={"Disclaimer"}
                                        value={form.text.it.disclaimer}
                                        setValue={setValue}
                                        path={"text.it.disclaimer"}
                                        checkErrorVisibility={checkErrorVisibility}
                                    />
                                </GridItem>
                            </GridRow>

                            <GridRow className="title-margin">
                                <GridItem>
                                    <h2>Inglese</h2>
                                </GridItem>
                            </GridRow>

                            <GridRow>
                                <GridItem className={"col-xs-12-12"}>
                                    <WysiwygSimpleEditor
                                        label={"Descrizione"}
                                        value={form.text.en.description}
                                        setValue={setValue}
                                        path={"text.en.description"}
                                        checkErrorVisibility={checkErrorVisibility}
                                    />
                                </GridItem>
                            </GridRow>

                            <GridRow>
                                <GridItem className={"col-xs-12-12"}>
                                    <WysiwygSimpleEditor
                                        label={"Manutenzione"}
                                        value={form.text.en.maintenance}
                                        setValue={setValue}
                                        path={"text.en.maintenance"}
                                        checkErrorVisibility={checkErrorVisibility}
                                    />
                                </GridItem>
                            </GridRow>

                            <GridRow>
                                <GridItem className={"col-xs-12-12"}>
                                    <WysiwygSimpleEditor
                                        label={"Disclaimer"}
                                        value={form.text.en.disclaimer}
                                        setValue={setValue}
                                        path={"text.en.disclaimer"}
                                        checkErrorVisibility={checkErrorVisibility}
                                    />
                                </GridItem>
                            </GridRow>
                        </GridColumn>
                    </Paper>
                </GridItem>
            </GridRow>
        </GridItem>
    );
}

export default MaterialForm;
